import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

/**
 * Staff sign-in (2026-08-18 — the dual-rail flow).
 *
 * One box: login email OR mobile number. `POST /api/login-link` sends the
 * sign-in EMAIL (link + 8-digit code, via Resend) and, when the row has a
 * phone, the same code over WHATSAPP (`staff_login_code` template). The
 * caller then either taps the emailed link (handled implicitly —
 * `detectSessionInUrl` parses the hash on load) or types the code here,
 * which `POST /api/login-link/verify` exchanges for a session server-side —
 * so a person who typed their PHONE never has to tell us their email.
 *
 * The response is deliberately uniform whether or not the identifier maps
 * to a staff row (non-enumeration — the server explains why), so the copy
 * below never claims a send definitely happened; the masked `hint` is shown
 * when the server chose to reveal one.
 */

const STALE_MESSAGES: Record<string, string> = {
  studio_stale_session:
    "Your session referenced a studio that no longer exists in the database (most likely a db:reset since you signed in). Sign in again to get a fresh session bound to the current studio row.",
  studio_not_found:
    "Your session referenced a studio that no longer exists. Sign in again to get a fresh session.",
  studio_unresolved:
    "Your session has no studio assigned. An admin needs to set app_metadata.studio_id before you can sign in.",
};

export function Login(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const staleReason = params.get("reason") === "stale_session";
  const staleCode = params.get("code");
  const staleBanner = staleReason
    ? (STALE_MESSAGES[staleCode ?? ""] ?? STALE_MESSAGES["studio_stale_session"]!)
    : null;
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState<{ hint: string | null } | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const requestLink = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch("/api/login-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier }),
      });
      if (!res.ok) {
        setMsg({
          kind: "err",
          text: "Sign-in is unavailable right now. Try again in a moment.",
        });
        return;
      }
      const data = (await res.json()) as { ok: boolean; hint?: string | null };
      setSent({ hint: data.hint ?? null });
    } catch {
      setMsg({ kind: "err", text: "Network error. Try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch("/api/login-link/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, code: code.trim() }),
      });
      const data = (await res.json()) as {
        ok: boolean;
        access_token?: string;
        refresh_token?: string;
      };
      if (!data.ok || !data.access_token || !data.refresh_token) {
        setMsg({
          kind: "err",
          text: "That code didn't work. Codes are 8 digits and expire after 1 hour — check for a newer message, or request a fresh one.",
        });
        return;
      }
      const { error } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (error) {
        setMsg({ kind: "err", text: "Could not start the session. Try again." });
        return;
      }
      navigate("/", { replace: true });
    } catch {
      setMsg({ kind: "err", text: "Network error. Try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-card">
      <h1>Clavis · Dashboard</h1>
      {staleBanner && (
        <div
          className="msg"
          style={{
            color: "#7a4e00",
            background: "#f7e9c6",
            border: "1px solid #fcd34d",
            padding: "10px 12px",
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <strong>Stale session.</strong> {staleBanner}
        </div>
      )}
      {sent === null ? (
        <form onSubmit={(e) => void requestLink(e)}>
          <input
            type="text"
            placeholder="email or mobile number"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
          <button type="submit" disabled={busy}>
            {busy ? "Sending…" : "Send sign-in link"}
          </button>
        </form>
      ) : (
        <>
          <div className="msg" style={{ color: "#16693a" }}>
            {sent.hint !== null
              ? `Sent: ${sent.hint}.`
              : "If that matches a staff account, a sign-in link and code are on their way."}{" "}
            Tap the emailed link, or enter the 8-digit code below.
          </div>
          <form onSubmit={(e) => void verifyCode(e)}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="8-digit code"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <button type="submit" disabled={busy}>
              {busy ? "Checking…" : "Sign in with code"}
            </button>
          </form>
          <button
            type="button"
            className="linklike"
            style={{ marginTop: 8 }}
            onClick={() => {
              setSent(null);
              setCode("");
              setMsg(null);
            }}
          >
            Use a different email or number
          </button>
        </>
      )}
      {msg && (
        <div
          className="msg"
          style={{ color: msg.kind === "err" ? "#8a1024" : "#16693a" }}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}
